import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Mails, Package, PlusCircle, Image, LogOut, Loader2,
  Save, Trash2, Plus, ArrowLeft, Upload, Mail, MessageCircle, Archive,
  ArchiveRestore, CalendarDays, ChevronLeft, ChevronRight, Award, AlertTriangle,
  TrendingUp, CheckCircle2, DollarSign, Quote, HelpCircle, Star, GripVertical, Film, Sparkles, Settings,
} from "lucide-react";
import { useContent } from "../lib/content";
import { ServicesTab } from "./admin/ServicesTab";
import { SettingsTab } from "./admin/SettingsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.type === "image/gif" || file.size < 1024 * 1024) return resolve(file);
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 2000;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const isPng = file.type === "image/png";
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (blob && blob.size < file.size) {
            const name = isPng ? file.name : file.name.replace(/\.\w+$/, "") + ".jpg";
            resolve(new File([blob], name, { type: blob.type }));
          } else {
            resolve(file);
          }
        },
        isPng ? "image/png" : "image/jpeg",
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const inputCls =
  "w-full rounded-xl bg-white border border-black/10 px-4 py-2.5 text-sm focus:border-[#c9006f] transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#121212]/45 mb-1.5";

const WelcomeSplash = () => (
  <div className="min-h-screen bg-[#3c003a] grid place-items-center px-6" data-testid="admin-welcome-splash">
    <div className="text-center">
      <motion.img
        src="/logo.png"
        alt="GlamBooth Cayman"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="h-40 w-auto object-contain mx-auto mb-8 brightness-110 drop-shadow-[0_0_24px_rgba(255,255,255,0.5)]"
      />
      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="font-serif text-white text-4xl sm:text-5xl mb-6"
      >
        Welcome <em className="text-[#c9006f]">Michele</em>
      </motion.h1>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="flex justify-center gap-2"
      >
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.25, 1] }}
            transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.22 }}
            className="w-2.5 h-2.5 rounded-full bg-[#c9006f]"
          />
        ))}
      </motion.div>
    </div>
  </div>
);

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const formatDetail = (d) => {
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
    return "Login failed. Please try again.";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data.token);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3c003a] grid place-items-center px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
        <img src="/logo.png" alt="GlamBooth Cayman" className="h-20 w-auto object-contain mx-auto mb-4" />
        <h1 className="font-serif text-2xl text-center text-[#121212] mb-6">Admin Panel</h1>
        <div className="space-y-4">
          <input
            className={inputCls}
            type="email"
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
          />
          <input
            className={inputCls}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="btn-gold w-full rounded-full py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </div>
        <Link to="/" className="block text-center text-xs text-[#91008d] mt-6 hover:underline">
          ← Back to website
        </Link>
      </form>
    </div>
  );
};

const handleAuthError = (e) => {
  if (e?.response?.status === 401) {
    localStorage.removeItem("gb_admin_token");
    window.location.reload();
  }
};

const STATUSES = [
  { key: "New", cls: "bg-[#c9006f] text-white", idle: "border-[#c9006f]/40 text-[#c9006f]" },
  { key: "Contacted", cls: "bg-amber-500 text-white", idle: "border-amber-400/60 text-amber-600" },
  { key: "Booked", cls: "bg-emerald-600 text-white", idle: "border-emerald-500/50 text-emerald-600" },
];

const InquiriesTab = ({ token }) => {
  const [items, setItems] = useState(null);
  const [view, setView] = useState("active");
  const [sendingReview, setSendingReview] = useState("");
  useEffect(() => {
    axios.get(`${API}/inquiries`, auth(token)).then((r) => setItems(r.data.reverse())).catch((e) => {
      handleAuthError(e);
      setItems([]);
    });
  }, [token]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status }, auth(token));
      setItems((arr) => arr.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      handleAuthError(e);
      toast.error("Status update failed");
    }
  };
  const setArchived = async (id, archived) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/archive`, { archived }, auth(token));
      setItems((arr) => arr.map((q) => (q.id === id ? { ...q, archived } : q)));
      toast.success(archived ? "Inquiry archived" : "Inquiry restored");
    } catch (e) {
      handleAuthError(e);
      toast.error("Archive update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this inquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/inquiries/${id}`, auth(token));
      setItems((arr) => arr.filter((q) => q.id !== id));
      toast.success("Inquiry deleted");
    } catch (e) {
      handleAuthError(e);
      toast.error("Delete failed");
    }
  };

  const sendReview = async (id) => {
    if (!window.confirm("Send a review request email to this client now?")) return;
    setSendingReview(id);
    try {
      await axios.post(`${API}/inquiries/${id}/send-review-request`, {}, auth(token));
      setItems((arr) => arr.map((q) => (q.id === id ? { ...q, review_request_sent: true } : q)));
      toast.success("Review request sent");
    } catch (e) {
      handleAuthError(e);
      toast.error("Failed to send review request");
    }
    setSendingReview("");
  };

  const replyBody = (q) =>
    encodeURIComponent(
      `Hi ${q.name.split(" ")[0]},\n\nThank you for your inquiry with GlamBooth Cayman! We'd love to be part of your ${q.event_type || "event"} on ${q.event_date}${q.venue ? ` at ${q.venue}` : ""}.\n\nYour selection: ${q.package_interest}${q.addons?.length ? ` + ${q.addons.join(", ")}` : ""}${q.estimated_total ? ` (est. ${q.estimated_total})` : ""}.\n\nBest,\nMichele — GlamBooth Cayman`
    );

  if (!items) return <Loader2 className="animate-spin text-[#c9006f] mx-auto mt-10" />;

  const shown = items.filter((q) => (view === "archived" ? q.archived : !q.archived));
  const archivedCount = items.filter((q) => q.archived).length;
  const dateCounts = items.filter((q) => !q.archived).reduce((m, q) => {
    if (q.event_date) m[q.event_date] = (m[q.event_date] || 0) + 1;
    return m;
  }, {});

  return (
    <div className="space-y-4" data-testid="admin-inquiries-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#121212]/50">{shown.length} {view === "archived" ? "archived" : "active"} inquiries</p>
        <div className="flex gap-2">
          <button
            onClick={() => setView("active")}
            data-testid="inquiries-view-active-btn"
            className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-all ${view === "active" ? "bg-[#91008d] text-white border-transparent" : "bg-white border-black/10 text-[#121212]/60"}`}
          >
            Active
          </button>
          <button
            onClick={() => setView("archived")}
            data-testid="inquiries-view-archived-btn"
            className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-all ${view === "archived" ? "bg-[#91008d] text-white border-transparent" : "bg-white border-black/10 text-[#121212]/60"}`}
          >
            Archived ({archivedCount})
          </button>
        </div>
      </div>
      {shown.map((q) => (
        <div key={q.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid="admin-inquiry-row">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <p className="font-semibold text-[#121212]">{q.name} <span className="text-[#121212]/40 font-normal">· {q.email}{q.phone ? ` · ${q.phone}` : ""}</span></p>
            <span className="flex items-center gap-3">
              {!q.archived && dateCounts[q.event_date] > 1 && (
                <span
                  data-testid="inquiry-conflict-badge"
                  title={`${dateCounts[q.event_date]} inquiries want ${q.event_date}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-700 px-3 py-1 text-[11px] font-bold"
                >
                  <AlertTriangle size={12} /> Date conflict ({dateCounts[q.event_date]})
                </span>
              )}
              <span className="font-serif text-[#c9006f] text-lg">{q.estimated_total || "—"}</span>
            </span>
          </div>
          <p className="text-sm text-[#121212]/60">
            {q.event_date} · {q.venue || "No venue"} · {q.event_type || "Event type n/a"}
            {q.guest_count ? ` · ${q.guest_count} guests` : ""}
          </p>
          <p className="text-sm text-[#91008d] mt-1">{q.package_interest}{q.addons?.length ? ` + ${q.addons.join(", ")}` : ""}</p>
          {q.message && <p className="text-sm text-[#121212]/50 mt-2 italic">"{q.message}"</p>}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="flex gap-2" data-testid="inquiry-status-picker">
              {STATUSES.map((s) => {
                const active = (q.status || "New") === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => !active && setStatus(q.id, s.key)}
                    data-testid={`status-${s.key.toLowerCase()}-btn`}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-all ${
                      active ? `${s.cls} border-transparent shadow-sm` : `bg-white ${s.idle} hover:opacity-80`
                    }`}
                  >
                    {s.key}
                  </button>
                );
              })}
            </div>
            <span className="flex-1" />
            {q.status === "Booked" &&
              (q.review_request_sent ? (
                <span
                  data-testid="inquiry-review-sent-badge"
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 px-4 py-1.5 text-xs font-bold"
                >
                  <CheckCircle2 size={13} /> Review requested
                </span>
              ) : (
                <button
                  onClick={() => sendReview(q.id)}
                  disabled={sendingReview === q.id}
                  data-testid="inquiry-send-review-btn"
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold border border-[#c9006f]/40 text-[#c9006f] hover:bg-[#c9006f] hover:text-white transition-colors disabled:opacity-60"
                >
                  {sendingReview === q.id ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />} Request review
                </button>
              ))}
            <a
              href={`mailto:${q.email}?subject=${encodeURIComponent("Your GlamBooth Cayman Inquiry — " + q.event_date)}&body=${replyBody(q)}`}
              data-testid="inquiry-reply-email-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold border border-[#91008d]/30 text-[#91008d] hover:bg-[#91008d] hover:text-white transition-colors"
            >
              <Mail size={13} /> Email Reply
            </a>
            {q.phone && (
              <a
                href={`https://wa.me/${q.phone.replace(/\D/g, "")}?text=${replyBody(q)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="inquiry-reply-whatsapp-btn"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold border border-[#25D366]/50 text-[#128C4A] hover:bg-[#25D366] hover:text-white transition-colors"
              >
                <MessageCircle size={13} /> WhatsApp
              </a>
            )}
            <button
              onClick={() => setArchived(q.id, !q.archived)}
              data-testid="inquiry-archive-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold border border-black/15 text-[#121212]/60 hover:border-[#91008d] hover:text-[#91008d] transition-colors"
            >
              {q.archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
              {q.archived ? "Restore" : "Archive"}
            </button>
            <button
              onClick={() => remove(q.id)}
              data-testid="inquiry-delete-btn"
              aria-label="Delete inquiry"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
      {shown.length === 0 && <p className="text-[#121212]/40 text-sm">No {view === "archived" ? "archived" : "active"} inquiries.</p>}
    </div>
  );
};

const SubscribersTab = ({ token }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/newsletter`, auth(token)).then((r) => setItems(r.data.reverse())).catch((e) => {
      handleAuthError(e);
      setItems([]);
    });
  }, [token]);
  if (!items) return <Loader2 className="animate-spin text-[#c9006f] mx-auto mt-10" />;
  return (
    <div data-testid="admin-subscribers-tab">
      <p className="text-sm text-[#121212]/50 mb-4">{items.length} subscribers</p>
      <div className="bg-white rounded-2xl border border-black/5 divide-y divide-black/5 shadow-sm">
        {items.map((s) => (
          <div key={s.id} className="px-5 py-3 flex justify-between text-sm" data-testid="admin-subscriber-row">
            <span className="text-[#121212]">{s.email}</span>
            <span className="text-[#121212]/40">{new Date(s.created_at).toLocaleDateString()}</span>
          </div>
        ))}
        {items.length === 0 && <p className="px-5 py-4 text-[#121212]/40 text-sm">No subscribers yet.</p>}
      </div>
    </div>
  );
};

const PackagesTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState("");
  const [draft, setDraft] = useState({ name: "", price: "", hours: "", tagline: "" });
  const load = useCallback(() => axios.get(`${API}/content/packages`).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const update = (id, k, v) => setItems((arr) => arr.map((p) => (p.id === id ? { ...p, [k]: v } : p)));
  const save = async (p) => {
    setSaving(p.id);
    try {
      await axios.put(`${API}/content/packages/${p.id}`, {
        name: p.name, price: Number(p.price), hours: p.hours, tagline: p.tagline, popular: p.popular,
        price_suffix: p.price_suffix || "",
        features: (p.features || []).map((s) => s.trim()).filter(Boolean),
        tiers: p.tiers || [],
      }, auth(token));
      toast.success(`${p.name} saved`);
      refreshContent();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving("");
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete the "${p.name}" package? It will disappear from the website immediately.`)) return;
    try {
      await axios.delete(`${API}/content/packages/${p.id}`, auth(token));
      toast.success("Package deleted");
      load(); refreshContent();
    } catch { toast.error("Delete failed"); }
  };
  const create = async () => {
    if (!draft.name || !draft.price) { toast.error("Name and price required"); return; }
    try {
      await axios.post(`${API}/content/packages`, {
        name: draft.name, price: Number(draft.price), hours: draft.hours || "3 hours", tagline: draft.tagline, popular: false,
      }, auth(token));
      toast.success("Package created — now live on the website");
      setDraft({ name: "", price: "", hours: "", tagline: "" });
      load(); refreshContent();
    } catch { toast.error("Create failed"); }
  };
  return (
    <div className="space-y-5" data-testid="admin-packages-tab">
      <div className="grid md:grid-cols-3 gap-5">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm space-y-3" data-testid={`admin-package-${p.id}`}>
            <div><span className={labelCls}>Name</span><input className={inputCls} value={p.name} onChange={(e) => update(p.id, "name", e.target.value)} data-testid="admin-package-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={labelCls}>Price ($)</span><input className={inputCls} type="number" value={p.price} onChange={(e) => update(p.id, "price", e.target.value)} data-testid="admin-package-price-input" /></div>
              <div><span className={labelCls}>Hours</span><input className={inputCls} value={p.hours} onChange={(e) => update(p.id, "hours", e.target.value)} /></div>
            </div>
            <div><span className={labelCls}>Tagline</span><input className={inputCls} value={p.tagline} onChange={(e) => update(p.id, "tagline", e.target.value)} /></div>
            <div><span className={labelCls}>Price note (shown after $, e.g. "up to 50 guests" or "per hour")</span><input className={inputCls} value={p.price_suffix || ""} placeholder="defaults to hours" onChange={(e) => update(p.id, "price_suffix", e.target.value)} data-testid="admin-package-price-suffix-input" /></div>
            <div><span className={labelCls}>Features (one per line, blank = default list)</span><textarea className={`${inputCls} resize-none`} rows={4} value={(p.features || []).join("\n")} onChange={(e) => update(p.id, "features", e.target.value.split("\n"))} data-testid="admin-package-features-input" /></div>
            <label className="flex items-center gap-2 text-sm text-[#121212]/70">
              <input type="checkbox" checked={p.popular} onChange={(e) => update(p.id, "popular", e.target.checked)} className="accent-[#c9006f]" />
              Most Popular badge
            </label>
            <div className="flex gap-3">
              <button onClick={() => save(p)} disabled={saving === p.id} data-testid="admin-package-save-btn" className="btn-gold flex-1 rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {saving === p.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
              <button onClick={() => remove(p)} data-testid="admin-package-delete-btn" className="rounded-full px-4 border border-red-200 text-red-500 hover:bg-red-50 transition-colors" aria-label="Delete package"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-[#f8eef6] rounded-2xl border border-[#c9006f]/20 p-5" data-testid="admin-package-create-form">
        <p className="font-serif text-lg text-[#121212] mb-3">New Package</p>
        <div className="grid md:grid-cols-[1.2fr_100px_110px_1.4fr_auto] gap-3 items-end">
          <div><span className={labelCls}>Name</span><input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="admin-new-package-name" /></div>
          <div><span className={labelCls}>Price ($)</span><input className={inputCls} type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="admin-new-package-price" /></div>
          <div><span className={labelCls}>Hours</span><input className={inputCls} value={draft.hours} placeholder="3 hours" onChange={(e) => setDraft({ ...draft, hours: e.target.value })} data-testid="admin-new-package-hours" /></div>
          <div><span className={labelCls}>Tagline</span><input className={inputCls} value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} data-testid="admin-new-package-tagline" /></div>
          <button onClick={create} data-testid="admin-new-package-create-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><Plus size={14} /> Add</button>
        </div>
      </div>
    </div>
  );
};

const AddonsTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ title: "", price: "", unit: "", desc: "" });
  const load = useCallback(() => axios.get(`${API}/content/addons`).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const update = (id, k, v) => setItems((arr) => arr.map((a) => (a.id === id ? { ...a, [k]: v } : a)));
  const save = async (a) => {
    try {
      await axios.put(`${API}/content/addons/${a.id}`, { title: a.title, price: Number(a.price), unit: a.unit, desc: a.desc }, auth(token));
      toast.success("Add-on saved");
      refreshContent();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/content/addons/${id}`, auth(token));
      toast.success("Add-on deleted");
      load(); refreshContent();
    } catch { toast.error("Delete failed"); }
  };
  const create = async () => {
    if (!draft.title || !draft.price) { toast.error("Title and price required"); return; }
    try {
      await axios.post(`${API}/content/addons`, { ...draft, price: Number(draft.price) }, auth(token));
      toast.success("Add-on created");
      setDraft({ title: "", price: "", unit: "", desc: "" });
      load(); refreshContent();
    } catch { toast.error("Create failed"); }
  };
  return (
    <div className="space-y-5" data-testid="admin-addons-tab">
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm space-y-3" data-testid={`admin-addon-${a.id}`}>
            <div className="grid grid-cols-[1fr_90px_80px] gap-3">
              <div><span className={labelCls}>Title</span><input className={inputCls} value={a.title} onChange={(e) => update(a.id, "title", e.target.value)} /></div>
              <div><span className={labelCls}>Price ($)</span><input className={inputCls} type="number" value={a.price} onChange={(e) => update(a.id, "price", e.target.value)} /></div>
              <div><span className={labelCls}>Unit</span><input className={inputCls} value={a.unit} placeholder="/hr" onChange={(e) => update(a.id, "unit", e.target.value)} /></div>
            </div>
            <div><span className={labelCls}>Description</span><textarea className={`${inputCls} resize-none`} rows={2} value={a.desc} onChange={(e) => update(a.id, "desc", e.target.value)} /></div>
            <div className="flex gap-3">
              <button onClick={() => save(a)} data-testid="admin-addon-save-btn" className="btn-gold flex-1 rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2"><Save size={14} /> Save</button>
              <button onClick={() => remove(a.id)} data-testid="admin-addon-delete-btn" className="rounded-full px-4 border border-red-200 text-red-500 hover:bg-red-50 transition-colors" aria-label="Delete add-on"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-[#f8eef6] rounded-2xl border border-[#c9006f]/20 p-5" data-testid="admin-addon-create-form">
        <p className="font-serif text-lg text-[#121212] mb-3">New Add-On</p>
        <div className="grid md:grid-cols-[1fr_100px_90px_1.4fr_auto] gap-3 items-end">
          <div><span className={labelCls}>Title</span><input className={inputCls} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="admin-new-addon-title" /></div>
          <div><span className={labelCls}>Price ($)</span><input className={inputCls} type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="admin-new-addon-price" /></div>
          <div><span className={labelCls}>Unit</span><input className={inputCls} value={draft.unit} placeholder="/hr" onChange={(e) => setDraft({ ...draft, unit: e.target.value })} /></div>
          <div><span className={labelCls}>Description</span><input className={inputCls} value={draft.desc} onChange={(e) => setDraft({ ...draft, desc: e.target.value })} /></div>
          <button onClick={create} data-testid="admin-new-addon-create-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><Plus size={14} /> Add</button>
        </div>
      </div>
    </div>
  );
};

const GalleryTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ src: "", label: "", cat: "Weddings" });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [replacingId, setReplacingId] = useState("");
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const loadCats = useCallback(() => axios.get(`${API}/content/gallery-categories`).then((r) => setCats(r.data)), []);
  useEffect(() => { loadCats(); }, [loadCats]);
  const load = useCallback(() => axios.get(`${API}/content/gallery`).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const bulkAdd = async (files) => {
    setUploading(true);
    let ok = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress(`${i + 1} of ${files.length}`);
        try {
          const toUpload = await compressImage(files[i]);
          const fd = new FormData();
          fd.append("file", toUpload);
          const { data } = await axios.post(`${API}/upload`, fd, auth(token));
          const label = files[i].name.replace(/\.\w+$/, "").replace(/[-_]+/g, " ").trim() || "GlamBooth moment";
          await axios.post(`${API}/content/gallery`, {
            src: data.url,
            label,
            cat: draft.cat || "Weddings",
          }, auth(token));
          ok++;
        } catch (err) {
          handleAuthError(err);
        }
      }
      toast.success(`${ok} of ${files.length} item${files.length > 1 ? "s" : ""} added to the gallery`);
      load(); refreshContent();
    } finally {
      setUploading(false);
      setProgress("");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const dropped = Array.from(e.dataTransfer.files || []).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (!dropped.length) { toast.error("Drop image or video files"); return; }
    bulkAdd(dropped);
  };


  const uploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        const toUpload = await compressImage(files[0]);
        const fd = new FormData();
        fd.append("file", toUpload);
        const { data } = await axios.post(`${API}/upload`, fd, auth(token));
        setDraft((d) => ({ ...d, src: data.url }));
        toast.success("Image uploaded — add a label and click Add");
      } else {
        await bulkAdd(files);
      }
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const update = (id, k, v) => setItems((arr) => arr.map((g) => (g.id === id ? { ...g, [k]: v } : g)));
  const save = async (g) => {
    try {
      await axios.put(`${API}/content/gallery/${g.id}`, { src: g.src, label: g.label, cat: g.cat, badge: g.badge || "" }, auth(token));
      toast.success("Photo saved");
      refreshContent();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/content/gallery/${id}`, auth(token));
      toast.success("Photo deleted");
      load(); refreshContent();
    } catch { toast.error("Delete failed"); }
  };
  const create = async () => {
    if (!draft.src || !draft.label) { toast.error("Image URL and label required"); return; }
    try {
      await axios.post(`${API}/content/gallery`, draft, auth(token));
      toast.success("Photo added");
      setDraft({ src: "", label: "", cat: "Weddings" });
      load(); refreshContent();
    } catch { toast.error("Create failed"); }
  };
  const startReplace = (id) => {
    setReplacingId(id);
    setTimeout(() => replaceRef.current?.click(), 0);
  };
  const addCat = async () => {
    if (!newCat.trim()) { toast.error("Category name required"); return; }
    try {
      await axios.post(`${API}/content/gallery-categories`, { name: newCat }, auth(token));
      setNewCat("");
      loadCats();
      toast.success("Category added");
    } catch (e) { handleAuthError(e); toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const editCat = (id, name) => setCats((arr) => arr.map((c) => (c.id === id ? { ...c, name } : c)));
  const saveCat = async (c) => {
    try {
      await axios.put(`${API}/content/gallery-categories/${c.id}`, { name: c.name }, auth(token));
      toast.success("Category renamed — photos updated");
      loadCats(); load(); refreshContent();
    } catch (e) { handleAuthError(e); toast.error(e.response?.data?.detail || "Rename failed"); }
  };
  const removeCat = async (c) => {
    if (!window.confirm(`Delete the "${c.name}" category?`)) return;
    try {
      await axios.delete(`${API}/content/gallery-categories/${c.id}`, auth(token));
      loadCats();
      toast.success("Category deleted");
    } catch (e) { handleAuthError(e); toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const [dragIdx, setDragIdx] = useState(null);
  const dropCat = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const arr = [...cats];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx, 0, moved);
    setCats(arr);
    setDragIdx(null);
    try {
      await axios.put(`${API}/content/gallery-categories/reorder`, { ids: arr.map((c) => c.id) }, auth(token));
      toast.success("Category order saved");
      refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Reorder failed"); loadCats(); }
  };
  const replaceFile = async (e) => {
    const file = e.target.files?.[0];
    const item = items.find((g) => g.id === replacingId);
    if (!file || !item) { setReplacingId(""); return; }
    setReplaceBusy(true);
    try {
      const toUpload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", toUpload);
      const { data } = await axios.post(`${API}/upload`, fd, auth(token));
      await axios.put(`${API}/content/gallery/${item.id}`, {
        src: data.url,
        label: item.label,
        cat: item.cat,
        badge: item.badge || "",
      }, auth(token));
      toast.success("Media replaced");
      load(); refreshContent();
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setReplaceBusy(false);
      setReplacingId("");
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };
  return (
    <div className="space-y-5" data-testid="admin-gallery-tab">
      <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" onChange={replaceFile} data-testid="admin-replace-file-input" />
      <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid="admin-gallery-categories">
        <p className="font-serif text-lg text-[#121212] mb-3">Gallery Categories</p>
        <div className="flex flex-wrap items-center gap-3">
          {cats.map((c, i) => (
            <div
              key={c.id}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropCat(i)}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-1 rounded-full border bg-[#f8eef6]/60 pl-1 pr-1.5 py-1 cursor-grab active:cursor-grabbing transition-all ${
                dragIdx === i ? "opacity-40 border-[#c9006f]" : "border-[#91008d]/25"
              }`}
              data-testid={`admin-category-${c.id}`}
            >
              <GripVertical size={13} className="text-[#91008d]/40 shrink-0" />
              <input
                className="bg-transparent text-sm text-[#121212] px-2.5 py-1 w-32 focus:outline-none"
                value={c.name}
                onChange={(e) => editCat(c.id, e.target.value)}
                data-testid={`admin-category-input-${c.id}`}
              />
              <button onClick={() => saveCat(c)} data-testid={`admin-category-save-${c.id}`} className="grid place-items-center w-7 h-7 rounded-full bg-[#91008d]/10 text-[#91008d] hover:bg-[#91008d] hover:text-white transition-colors" aria-label="Save category name">
                <Save size={12} />
              </button>
              <button onClick={() => removeCat(c)} data-testid={`admin-category-delete-${c.id}`} className="grid place-items-center w-7 h-7 rounded-full text-red-400 hover:bg-red-50 transition-colors" aria-label="Delete category">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input
              className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#c9006f] w-40"
              placeholder="New category..."
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCat()}
              data-testid="admin-new-category-input"
            />
            <button onClick={addCat} data-testid="admin-new-category-add-btn" className="btn-gold rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>
        <p className="text-xs text-[#121212]/40 mt-3">Drag chips to set the order gallery filters appear in. Renaming a category updates all its photos automatically. A category must be empty before it can be deleted.</p>
      </div>
      <div className="bg-[#f8eef6] rounded-2xl border border-[#c9006f]/20 p-5" data-testid="admin-gallery-create-form">
        <p className="font-serif text-lg text-[#121212] mb-3">Add Media</p>
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadFile} data-testid="admin-upload-file-input" />
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileRef.current?.click()}
          role="button"
          tabIndex={0}
          data-testid="admin-gallery-dropzone"
          className={`mb-4 rounded-2xl border-2 border-dashed px-6 py-8 text-center cursor-pointer transition-all duration-300 ${
            dragOver
              ? "border-[#c9006f] bg-[#c9006f]/10 scale-[1.01]"
              : "border-[#91008d]/30 bg-white/50 hover:border-[#c9006f] hover:bg-white"
          }`}
        >
          <div className="flex flex-col items-center gap-2 pointer-events-none">
            {uploading ? <Loader2 size={26} className="animate-spin text-[#c9006f]" /> : <Upload size={26} className="text-[#c9006f]" />}
            <p className="text-sm font-bold text-[#121212]">
              {uploading
                ? (progress ? `Uploading ${progress}...` : "Uploading...")
                : dragOver
                ? "Drop to upload!"
                : "Drag & drop photos or videos here — or click to browse"}
            </p>
            <p className="text-xs text-[#121212]/45">
              Dropped media is added straight to the "{draft.cat || "Weddings"}" category · photos any size (auto-compressed) · videos up to 100MB
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-[2fr_1.2fr_1fr_auto] gap-3 items-end">
          <div><span className={labelCls}>Image URL</span><input className={inputCls} value={draft.src} placeholder="https://..." onChange={(e) => setDraft({ ...draft, src: e.target.value })} data-testid="admin-new-photo-src" /></div>
          <div><span className={labelCls}>Label</span><input className={inputCls} value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="admin-new-photo-label" /></div>
          <div><span className={labelCls}>Category</span>
            <select className={inputCls} value={draft.cat} onChange={(e) => setDraft({ ...draft, cat: e.target.value })} data-testid="admin-new-photo-cat-select">
              {!cats.some((c) => c.name === draft.cat) && <option value={draft.cat}>{draft.cat}</option>}
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <button onClick={create} data-testid="admin-new-photo-create-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><Plus size={14} /> Add</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((g) => (
          <div key={g.id} className="bg-white rounded-2xl border border-black/5 overflow-hidden shadow-sm" data-testid={`admin-photo-${g.id}`}>
            <div className="relative group/media">
              {/\.(mp4|webm|mov)(\?|$)/i.test(g.src) ? (
                <video src={g.src} muted loop autoPlay playsInline preload="metadata" className="w-full h-36 object-cover" />
              ) : (
                <img src={g.src} alt={g.label} loading="lazy" decoding="async" className="w-full h-36 object-cover" />
              )}
              {g.badge && (
                <span className="absolute top-2 left-2 rounded-full bg-[#c9006f] text-white text-[9px] font-bold tracking-[0.14em] uppercase px-2.5 py-1">
                  {{ "360": "360°", video: "Video", boomerang: "Boomerang" }[g.badge] || g.badge}
                </span>
              )}
              <button
                onClick={() => startReplace(g.id)}
                disabled={replaceBusy}
                data-testid={`admin-photo-replace-btn-${g.id}`}
                className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-black/10 text-[#91008d] px-3.5 py-1.5 text-xs font-bold shadow-sm hover:bg-[#91008d] hover:text-white transition-colors disabled:opacity-60"
              >
                {replaceBusy && replacingId === g.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                Replace
              </button>
            </div>
            <div className="p-4 space-y-2.5">
              <input className={inputCls} value={g.label} onChange={(e) => update(g.id, "label", e.target.value)} />
              <select className={inputCls} value={g.cat} onChange={(e) => update(g.id, "cat", e.target.value)} data-testid={`admin-photo-cat-select-${g.id}`}>
                {!cats.some((c) => c.name === g.cat) && <option value={g.cat}>{g.cat}</option>}
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.14em] text-[#121212]/40 font-semibold shrink-0">Badge</span>
                <select className={inputCls} value={g.badge || ""} onChange={(e) => update(g.id, "badge", e.target.value)} data-testid={`admin-photo-badge-select-${g.id}`}>
                  <option value="">None</option>
                  <option value="360">360°</option>
                  <option value="video">Video</option>
                  <option value="boomerang">Boomerang</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={() => save(g)} data-testid="admin-photo-save-btn" className="btn-gold flex-1 rounded-full py-2 text-xs font-bold flex items-center justify-center gap-1.5"><Save size={12} /> Save</button>
                <button onClick={() => remove(g.id)} data-testid="admin-photo-delete-btn" className="rounded-full px-3.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors" aria-label="Delete photo"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatsBar = ({ token }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, auth(token)).then((r) => setStats(r.data)).catch(handleAuthError);
  }, [token]);
  if (!stats) return null;
  const cards = [
    { label: `Inquiries · ${stats.month}`, value: stats.inquiries_this_month, icon: TrendingUp, tid: "stat-inquiries" },
    { label: `Booked Events · ${stats.month}`, value: stats.bookings_this_month, icon: CheckCircle2, tid: "stat-bookings" },
    { label: `Est. Revenue · ${stats.month}`, value: `$${stats.revenue_this_month.toLocaleString()}`, icon: DollarSign, tid: "stat-revenue" },
    { label: "All-Time Inquiries", value: stats.total_inquiries, icon: Inbox, tid: "stat-total" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-bar">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.tid} data-testid={c.tid} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm flex items-center gap-4">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#91008d]/10 text-[#91008d] shrink-0">
              <Icon size={20} strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
              <p className="font-serif text-2xl text-[#121212] leading-none">{c.value}</p>
              <p className="text-[11px] uppercase tracking-[0.1em] font-semibold text-[#121212]/45 mt-1.5 truncate">{c.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BrandsTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: "", logo_src: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => axios.get(`${API}/content/brands`).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const toUpload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", toUpload);
      const { data } = await axios.post(`${API}/upload`, fd, auth(token));
      setDraft((d) => ({ ...d, logo_src: data.url }));
      toast.success("Logo uploaded — add the brand name and click Add");
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const update = (id, k, v) => setItems((arr) => arr.map((b) => (b.id === id ? { ...b, [k]: v } : b)));
  const save = async (b) => {
    try {
      await axios.put(`${API}/content/brands/${b.id}`, { name: b.name, logo_src: b.logo_src }, auth(token));
      toast.success("Brand saved");
      refreshContent();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/content/brands/${id}`, auth(token));
      toast.success("Brand deleted");
      load(); refreshContent();
    } catch { toast.error("Delete failed"); }
  };
  const create = async () => {
    if (!draft.name) { toast.error("Brand name required"); return; }
    try {
      await axios.post(`${API}/content/brands`, draft, auth(token));
      toast.success("Brand added");
      setDraft({ name: "", logo_src: "" });
      load(); refreshContent();
    } catch { toast.error("Create failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-brands-tab">
      <div className="bg-[#f8eef6] rounded-2xl border border-[#c9006f]/20 p-5" data-testid="admin-brand-create-form">
        <p className="font-serif text-lg text-[#121212] mb-3">Add Brand</p>
        <div className="flex items-center gap-3 mb-4">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={uploadFile} data-testid="admin-brand-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-brand-upload-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : "Upload logo"}
          </button>
          <span className="text-xs text-[#121212]/45">logo optional — brands without a logo show as styled text</span>
        </div>
        <div className="grid md:grid-cols-[1.2fr_2fr_auto] gap-3 items-end">
          <div><span className={labelCls}>Brand name</span><input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="admin-new-brand-name" /></div>
          <div><span className={labelCls}>Logo URL (optional)</span><input className={inputCls} value={draft.logo_src} placeholder="https://..." onChange={(e) => setDraft({ ...draft, logo_src: e.target.value })} data-testid="admin-new-brand-logo" /></div>
          <button onClick={create} data-testid="admin-new-brand-create-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><Plus size={14} /> Add</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm space-y-3" data-testid={`admin-brand-${b.id}`}>
            <div className="h-16 grid place-items-center bg-[#faf7f9] rounded-xl">
              {b.logo_src ? (
                <img src={b.logo_src} alt={b.name} className="max-h-12 w-auto object-contain" />
              ) : (
                <span className="font-serif italic text-xl text-[#3c003a]/60">{b.name}</span>
              )}
            </div>
            <div><span className={labelCls}>Name</span><input className={inputCls} value={b.name} onChange={(e) => update(b.id, "name", e.target.value)} /></div>
            <div><span className={labelCls}>Logo URL</span><input className={inputCls} value={b.logo_src} placeholder="(text only)" onChange={(e) => update(b.id, "logo_src", e.target.value)} /></div>
            <div className="flex gap-2">
              <button onClick={() => save(b)} data-testid="admin-brand-save-btn" className="btn-gold flex-1 rounded-full py-2 text-xs font-bold flex items-center justify-center gap-1.5"><Save size={12} /> Save</button>
              <button onClick={() => remove(b.id)} data-testid="admin-brand-delete-btn" aria-label="Delete brand" className="rounded-full px-3.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const STATUS_DOT = { New: "bg-[#c9006f]", Contacted: "bg-amber-500", Booked: "bg-emerald-600" };

const CalendarTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const today = new Date();
  const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });

  useEffect(() => {
    axios.get(`${API}/inquiries`, auth(token)).then((r) => setItems(r.data)).catch(handleAuthError);
  }, [token]);

  const events = items.filter((q) => q.event_date && !q.archived);
  const byDate = events.reduce((acc, q) => {
    (acc[q.event_date] = acc[q.event_date] || []).push(q);
    return acc;
  }, {});

  const firstDay = new Date(ym.y, ym.m, 1).getDay();
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const dateKey = (d) => `${ym.y}-${String(ym.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) => ym.y === today.getFullYear() && ym.m === today.getMonth() && d === today.getDate();
  const shift = (dir) =>
    setYm(({ y, m }) => {
      const nm = m + dir;
      return { y: y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });

  return (
    <div data-testid="admin-calendar-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[#121212]" data-testid="calendar-month-label">
          {MONTH_NAMES[ym.m]} {ym.y}
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="calendar-prev-btn" aria-label="Previous month" className="w-9 h-9 rounded-full border border-black/10 grid place-items-center text-[#121212]/60 hover:border-[#c9006f] hover:text-[#c9006f] transition-colors"><ChevronLeft size={17} /></button>
          <button onClick={() => setYm({ y: today.getFullYear(), m: today.getMonth() })} data-testid="calendar-today-btn" className="rounded-full px-4 h-9 text-xs font-bold border border-black/10 text-[#121212]/60 hover:border-[#c9006f] hover:text-[#c9006f] transition-colors">Today</button>
          <button onClick={() => shift(1)} data-testid="calendar-next-btn" aria-label="Next month" className="w-9 h-9 rounded-full border border-black/10 grid place-items-center text-[#121212]/60 hover:border-[#c9006f] hover:text-[#c9006f] transition-colors"><ChevronRight size={17} /></button>
        </div>
      </div>
      <div className="flex gap-5 mb-4 text-xs text-[#121212]/60">
        {Object.entries(STATUS_DOT).map(([k, cls]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${cls}`} /> {k}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] uppercase tracking-[0.15em] font-semibold text-[#121212]/40 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const dayEvents = byDate[dateKey(d)] || [];
          return (
            <div
              key={d}
              data-testid={`calendar-day-${d}`}
              className={`min-h-[92px] rounded-xl border p-2 bg-white ${
                dayEvents.length > 1
                  ? "border-amber-400 shadow-[0_0_0_1px_#fbbf24]"
                  : isToday(d)
                  ? "border-[#c9006f] shadow-[0_0_0_1px_#c9006f]"
                  : "border-black/5"
              }`}
            >
              <p className={`text-xs font-bold mb-1.5 flex items-center gap-1 ${isToday(d) ? "text-[#c9006f]" : "text-[#121212]/50"}`}>
                {d}
                {dayEvents.length > 1 && <AlertTriangle size={11} className="text-amber-500" data-testid="calendar-conflict-icon" />}
              </p>
              <div className="space-y-1">
                {dayEvents.map((q) => (
                  <div
                    key={q.id}
                    data-testid="calendar-event"
                    title={`${q.name} — ${q.package_interest} (${q.status || "New"})`}
                    className={`${STATUS_DOT[q.status || "New"]} text-white text-[10px] font-semibold rounded-md px-1.5 py-1 truncate`}
                  >
                    {q.name}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TestimonialsTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ quote: "", name: "", event: "" });
  const load = useCallback(
    () => axios.get(`${API}/content/testimonials/all`, auth(token)).then((r) => setItems(r.data)).catch(handleAuthError),
    [token]
  );
  useEffect(() => { load(); }, [load]);
  const update = (id, k, v) => setItems((arr) => arr.map((t) => (t.id === id ? { ...t, [k]: v } : t)));
  const save = async (t) => {
    try {
      await axios.put(`${API}/content/testimonials/${t.id}`, { quote: t.quote, name: t.name, event: t.event }, auth(token));
      toast.success("Testimonial saved");
      refreshContent();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/content/testimonials/${id}`, auth(token));
      toast.success("Testimonial deleted");
      load(); refreshContent();
    } catch { toast.error("Delete failed"); }
  };
  const create = async () => {
    if (!draft.quote || !draft.name) { toast.error("Quote and name required"); return; }
    try {
      await axios.post(`${API}/content/testimonials`, draft, auth(token));
      toast.success("Testimonial added");
      setDraft({ quote: "", name: "", event: "" });
      load(); refreshContent();
    } catch { toast.error("Create failed"); }
  };
  const approve = async (id) => {
    try {
      await axios.patch(`${API}/content/testimonials/${id}/approve`, {}, auth(token));
      toast.success("Testimonial approved — now live on the site");
      load(); refreshContent();
    } catch { toast.error("Approve failed"); }
  };
  const pending = items.filter((t) => t.approved === false);
  const approvedItems = items.filter((t) => t.approved !== false);
  return (
    <div className="space-y-5" data-testid="admin-testimonials-tab">
      {pending.length > 0 && (
        <div className="bg-amber-50 rounded-2xl border border-amber-300 p-5" data-testid="admin-pending-reviews-section">
          <p className="font-serif text-lg text-[#121212] mb-1 flex items-center gap-2">
            <Star size={16} className="text-amber-500 fill-amber-500" /> Pending client reviews ({pending.length})
          </p>
          <p className="text-xs text-[#121212]/50 mb-4">Submitted via the review request email — approve to publish on the website.</p>
          <div className="space-y-3">
            {pending.map((t) => (
              <div key={t.id} className="bg-white rounded-xl border border-black/5 p-4" data-testid={`admin-pending-review-${t.id}`}>
                <span className="flex gap-0.5 mb-2">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} size={13} className="text-[#c9006f] fill-[#c9006f]" />
                  ))}
                </span>
                <p className="text-sm text-[#121212]/80 italic">"{t.quote}"</p>
                <p className="text-xs text-[#121212]/50 mt-2">— {t.name}{t.event ? ` · ${t.event}` : ""}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => approve(t.id)} data-testid={`admin-review-approve-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-4 py-2 text-xs font-bold hover:bg-emerald-700 transition-colors"><CheckCircle2 size={13} /> Approve & Publish</button>
                  <button onClick={() => remove(t.id)} data-testid={`admin-review-reject-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={13} /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-[#f8eef6] rounded-2xl border border-[#c9006f]/20 p-5" data-testid="admin-testimonial-create-form">
        <p className="font-serif text-lg text-[#121212] mb-3">New Testimonial</p>
        <div className="space-y-3">
          <div><span className={labelCls}>Quote</span><textarea className={`${inputCls} resize-none`} rows={2} value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} data-testid="admin-new-testimonial-quote" /></div>
          <div className="grid md:grid-cols-[1fr_1.4fr_auto] gap-3 items-end">
            <div><span className={labelCls}>Client name</span><input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="admin-new-testimonial-name" /></div>
            <div><span className={labelCls}>Event (e.g. Wedding · Venue)</span><input className={inputCls} value={draft.event} onChange={(e) => setDraft({ ...draft, event: e.target.value })} data-testid="admin-new-testimonial-event" /></div>
            <button onClick={create} data-testid="admin-new-testimonial-create-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><Plus size={14} /> Add</button>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {approvedItems.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm space-y-3" data-testid={`admin-testimonial-${t.id}`}>
            <div><span className={labelCls}>Quote</span><textarea className={`${inputCls} resize-none`} rows={3} value={t.quote} onChange={(e) => update(t.id, "quote", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={labelCls}>Name</span><input className={inputCls} value={t.name} onChange={(e) => update(t.id, "name", e.target.value)} /></div>
              <div><span className={labelCls}>Event</span><input className={inputCls} value={t.event} onChange={(e) => update(t.id, "event", e.target.value)} /></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => save(t)} data-testid={`admin-testimonial-save-${t.id}`} className="btn-gold flex-1 rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2"><Save size={14} /> Save</button>
              <button onClick={() => remove(t.id)} data-testid={`admin-testimonial-delete-${t.id}`} aria-label="Delete testimonial" className="rounded-full px-4 border border-red-200 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FAQ_SECTIONS_ADMIN = [
  { key: "general", title: "General" },
  { key: "pricing", title: "Pricing & Booking" },
  { key: "features", title: "Booth Features" },
  { key: "setup", title: "Setup & Service Areas" },
  { key: "weddings", title: "Weddings & Corporate" },
  { key: "policies", title: "Policies & Contact" },
];

const FaqsTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ section: "general", question: "", answer: "", bulletsText: "" });
  const load = useCallback(() => axios.get(`${API}/content/faqs`).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const update = (id, k, v) => setItems((arr) => arr.map((f) => (f.id === id ? { ...f, [k]: v } : f)));
  const payload = (f) => ({
    section: f.section,
    question: f.question,
    answer: f.answer,
    bullets: (f.bulletsText !== undefined ? f.bulletsText : (f.bullets || []).join("\n")).split("\n").map((b) => b.trim()).filter(Boolean),
    schema_text: f.schema_text || "",
  });
  const save = async (f) => {
    try {
      await axios.put(`${API}/content/faqs/${f.id}`, payload(f), auth(token));
      toast.success("FAQ saved");
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/content/faqs/${id}`, auth(token));
      toast.success("FAQ deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };
  const create = async () => {
    if (!draft.question || !draft.answer) { toast.error("Question and answer required"); return; }
    try {
      await axios.post(`${API}/content/faqs`, {
        section: draft.section,
        question: draft.question,
        answer: draft.answer,
        bullets: draft.bulletsText.split("\n").map((b) => b.trim()).filter(Boolean),
        schema_text: "",
      }, auth(token));
      toast.success("FAQ added");
      setDraft({ section: "general", question: "", answer: "", bulletsText: "" });
      load();
    } catch { toast.error("Create failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-faqs-tab">
      <div className="bg-[#f8eef6] rounded-2xl border border-[#c9006f]/20 p-5" data-testid="admin-faq-create-form">
        <p className="font-serif text-lg text-[#121212] mb-3">New FAQ</p>
        <div className="space-y-3">
          <div className="grid md:grid-cols-[220px_1fr] gap-3">
            <div>
              <span className={labelCls}>Section</span>
              <select className={inputCls} value={draft.section} onChange={(e) => setDraft({ ...draft, section: e.target.value })} data-testid="admin-new-faq-section">
                {FAQ_SECTIONS_ADMIN.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
              </select>
            </div>
            <div><span className={labelCls}>Question</span><input className={inputCls} value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} data-testid="admin-new-faq-question" /></div>
          </div>
          <div><span className={labelCls}>Answer</span><textarea className={`${inputCls} resize-none`} rows={3} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} data-testid="admin-new-faq-answer" /></div>
          <div><span className={labelCls}>Bullet points (optional, one per line)</span><textarea className={`${inputCls} resize-none`} rows={2} value={draft.bulletsText} onChange={(e) => setDraft({ ...draft, bulletsText: e.target.value })} /></div>
          <button onClick={create} data-testid="admin-new-faq-create-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2"><Plus size={14} /> Add FAQ</button>
        </div>
      </div>
      {FAQ_SECTIONS_ADMIN.map((sec) => {
        const secItems = items.filter((f) => f.section === sec.key);
        if (!secItems.length) return null;
        return (
          <div key={sec.key}>
            <h3 className="font-serif text-xl text-[#121212] mb-3">{sec.title} <span className="text-[#121212]/40 text-sm">({secItems.length})</span></h3>
            <div className="space-y-3">
              {secItems.map((f) => (
                <div key={f.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm space-y-3" data-testid={`admin-faq-${f.id}`}>
                  <div className="grid md:grid-cols-[200px_1fr] gap-3">
                    <div>
                      <span className={labelCls}>Section</span>
                      <select className={inputCls} value={f.section} onChange={(e) => update(f.id, "section", e.target.value)}>
                        {FAQ_SECTIONS_ADMIN.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                      </select>
                    </div>
                    <div><span className={labelCls}>Question</span><input className={inputCls} value={f.question} onChange={(e) => update(f.id, "question", e.target.value)} /></div>
                  </div>
                  <div><span className={labelCls}>Answer</span><textarea className={`${inputCls} resize-none`} rows={3} value={f.answer} onChange={(e) => update(f.id, "answer", e.target.value)} /></div>
                  <div><span className={labelCls}>Bullet points (one per line)</span><textarea className={`${inputCls} resize-none`} rows={f.bullets?.length ? 3 : 1} value={f.bulletsText !== undefined ? f.bulletsText : (f.bullets || []).join("\n")} onChange={(e) => update(f.id, "bulletsText", e.target.value)} /></div>
                  <div className="flex gap-2">
                    <button onClick={() => save(f)} data-testid={`admin-faq-save-${f.id}`} className="btn-gold rounded-full px-6 py-2 text-xs font-bold flex items-center gap-1.5"><Save size={12} /> Save</button>
                    <button onClick={() => remove(f.id)} data-testid={`admin-faq-delete-${f.id}`} aria-label="Delete FAQ" className="rounded-full px-3.5 py-2 border border-red-200 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ExperienceTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState("");
  const [replacingId, setReplacingId] = useState("");
  const [replaceBusy, setReplaceBusy] = useState(false);
  const replaceRef = useRef(null);
  const [strips, setStrips] = useState([]);
  const [stripTarget, setStripTarget] = useState(null);
  const [stripBusy, setStripBusy] = useState(false);
  const [savingStrip, setSavingStrip] = useState("");
  const stripRef = useRef(null);
  const load = useCallback(() => axios.get(`${API}/content/experience`).then((r) => setItems(r.data)), []);
  const loadStrips = useCallback(() => axios.get(`${API}/content/hero-strips`).then((r) => setStrips(r.data)), []);
  useEffect(() => { load(); loadStrips(); }, [load, loadStrips]);
  const update = (id, k, v) => setItems((arr) => arr.map((c) => (c.id === id ? { ...c, [k]: v } : c)));
  const save = async (c) => {
    setSaving(c.id);
    try {
      await axios.put(`${API}/content/experience/${c.id}`, { title: c.title, desc: c.desc, img: c.img }, auth(token));
      toast.success(`${c.title} saved`);
      refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Save failed"); }
    finally { setSaving(""); }
  };
  const startReplace = (id) => {
    setReplacingId(id);
    setTimeout(() => replaceRef.current?.click(), 0);
  };
  const replaceFile = async (e) => {
    const file = e.target.files?.[0];
    const item = items.find((c) => c.id === replacingId);
    if (!file || !item) { setReplacingId(""); return; }
    setReplaceBusy(true);
    try {
      const toUpload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", toUpload);
      const { data } = await axios.post(`${API}/upload`, fd, auth(token));
      await axios.put(`${API}/content/experience/${item.id}`, {
        title: item.title, desc: item.desc,
        img: data.url,
      }, auth(token));
      toast.success("Media replaced");
      load(); refreshContent();
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setReplaceBusy(false);
      setReplacingId("");
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };
  const startStripReplace = (id, idx) => {
    setStripTarget({ id, idx });
    setTimeout(() => stripRef.current?.click(), 0);
  };
  const stripReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    const strip = strips.find((s) => s.id === stripTarget?.id);
    if (!file || !strip) { setStripTarget(null); return; }
    setStripBusy(true);
    try {
      const toUpload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", toUpload);
      const { data } = await axios.post(`${API}/upload`, fd, auth(token));
      const photos = [...strip.photos];
      photos[stripTarget.idx] = data.url;
      await axios.put(`${API}/content/hero-strips/${strip.id}`, { caption: strip.caption, photos }, auth(token));
      toast.success("Strip photo replaced");
      loadStrips(); refreshContent();
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setStripBusy(false);
      setStripTarget(null);
      if (stripRef.current) stripRef.current.value = "";
    }
  };
  const updateStripCaption = (id, v) => setStrips((arr) => arr.map((s) => (s.id === id ? { ...s, caption: v } : s)));
  const saveStrip = async (s) => {
    setSavingStrip(s.id);
    try {
      await axios.put(`${API}/content/hero-strips/${s.id}`, { caption: s.caption, photos: s.photos }, auth(token));
      toast.success("Strip saved");
      refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Save failed"); }
    finally { setSavingStrip(""); }
  };
  return (
    <div className="space-y-5" data-testid="admin-experience-tab">
      <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" onChange={replaceFile} data-testid="admin-experience-replace-input" />
      <p className="text-sm text-[#121212]/50">These four cards appear in the "Every format. Every moment." section on the homepage. Replace the photo/video and edit the wording for each.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-black/5 overflow-hidden shadow-sm" data-testid={`admin-experience-card-${c.id}`}>
            <div className="relative">
              {/\.(mp4|webm|mov)(\?|$)/i.test(c.img) ? (
                <video src={c.img} muted loop autoPlay playsInline className="w-full h-44 object-cover" />
              ) : (
                <img src={c.img} alt={c.title} className="w-full h-44 object-cover" />
              )}
              <button
                onClick={() => startReplace(c.id)}
                disabled={replaceBusy}
                data-testid={`admin-experience-replace-btn-${c.id}`}
                className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-black/10 text-[#91008d] px-3.5 py-1.5 text-xs font-bold shadow-sm hover:bg-[#91008d] hover:text-white transition-colors disabled:opacity-60"
              >
                {replaceBusy && replacingId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                Replace media
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div><span className={labelCls}>Title</span><input className={inputCls} value={c.title} onChange={(e) => update(c.id, "title", e.target.value)} data-testid={`admin-experience-title-${c.id}`} /></div>
              <div><span className={labelCls}>Description</span><textarea className={`${inputCls} resize-none`} rows={2} value={c.desc} onChange={(e) => update(c.id, "desc", e.target.value)} data-testid={`admin-experience-desc-${c.id}`} /></div>
              <button onClick={() => save(c)} disabled={saving === c.id} data-testid={`admin-experience-save-${c.id}`} className="btn-gold w-full rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {saving === c.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        ))}
      </div>
      <input ref={stripRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={stripReplaceFile} data-testid="admin-strip-replace-input" />
      <div>
        <p className="font-serif text-xl text-[#121212] mt-4 mb-1">Hero Photo Strips</p>
        <p className="text-sm text-[#121212]/50 mb-4">The two polaroid-style photo strips floating in the homepage hero. Replace any photo and edit each strip's caption.</p>
        <div className="grid md:grid-cols-2 gap-5">
          {strips.map((s, si) => (
            <div key={s.id} className="bg-white rounded-2xl border border-black/5 p-4 shadow-sm space-y-3" data-testid={`admin-hero-strip-${si}`}>
              <div className="grid grid-cols-3 gap-2">
                {s.photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt={`Strip photo ${i + 1}`} className="w-full h-24 object-cover rounded-lg" />
                    <button
                      onClick={() => startStripReplace(s.id, i)}
                      disabled={stripBusy}
                      data-testid={`admin-strip-${si}-photo-${i}-replace`}
                      className="absolute bottom-1.5 right-1.5 grid place-items-center w-7 h-7 rounded-full bg-white/95 border border-black/10 text-[#91008d] shadow-sm hover:bg-[#91008d] hover:text-white transition-colors disabled:opacity-60"
                      aria-label={`Replace strip photo ${i + 1}`}
                    >
                      {stripBusy && stripTarget?.id === s.id && stripTarget?.idx === i ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    </button>
                  </div>
                ))}
              </div>
              <div><span className={labelCls}>Caption</span><input className={inputCls} value={s.caption} onChange={(e) => updateStripCaption(s.id, e.target.value)} data-testid={`admin-strip-caption-${si}`} /></div>
              <button onClick={() => saveStrip(s)} disabled={savingStrip === s.id} data-testid={`admin-strip-save-${si}`} className="btn-gold w-full rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {savingStrip === s.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Strip
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { key: "inquiries", label: "Inquiries", icon: Inbox },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "subscribers", label: "Subscribers", icon: Mails },
  { key: "packages", label: "Packages", icon: Package },
  { key: "addons", label: "Add-Ons", icon: PlusCircle },
  { key: "gallery", label: "Gallery", icon: Image },
  { key: "experience", label: "Experience", icon: Film },
  { key: "services", label: "Services", icon: Sparkles },
  { key: "brands", label: "Brands", icon: Award },
  { key: "testimonials", label: "Testimonials", icon: Quote },
  { key: "faqs", label: "FAQs", icon: HelpCircle },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("gb_admin_token"));
  const [tab, setTab] = useState("inquiries");
  const [splash, setSplash] = useState(true);
  const { refresh } = useContent();

  useEffect(() => {
    const t = setTimeout(() => setSplash(false), 2400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, auth(token)).catch(() => {
      localStorage.removeItem("gb_admin_token");
      setToken(null);
    });
  }, [token]);

  const onLogin = (t) => {
    localStorage.setItem("gb_admin_token", t);
    setToken(t);
  };
  const logout = () => {
    localStorage.removeItem("gb_admin_token");
    setToken(null);
  };

  if (splash) return <WelcomeSplash />;
  if (!token) return <AdminLogin onLogin={onLogin} />;

  return (
    <div className="min-h-screen bg-[#faf7f9]" data-testid="admin-dashboard">
      <header className="bg-[#3c003a] px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <LayoutDashboard size={20} className="text-[#c9006f]" />
          <h1 className="font-serif text-white text-xl">GlamBooth Admin</h1>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-white/60 hover:text-[#ffc4e4] text-sm flex items-center gap-1.5 transition-colors" data-testid="admin-view-site-link">
            <ArrowLeft size={14} /> View site
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-white/25 text-white/80 px-4 py-2 text-sm hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <StatsBar token={token} />
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                  tab === t.key
                    ? "bg-[#91008d] text-white shadow-[0_8px_24px_rgba(145,0,141,0.3)]"
                    : "bg-white border border-black/8 text-[#121212]/60 hover:border-[#c9006f]"
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>
        {tab === "inquiries" && <InquiriesTab token={token} />}
        {tab === "calendar" && <CalendarTab token={token} />}
        {tab === "subscribers" && <SubscribersTab token={token} />}
        {tab === "packages" && <PackagesTab token={token} refreshContent={refresh} />}
        {tab === "addons" && <AddonsTab token={token} refreshContent={refresh} />}
        {tab === "gallery" && <GalleryTab token={token} refreshContent={refresh} />}
        {tab === "experience" && <ExperienceTab token={token} refreshContent={refresh} />}
        {tab === "services" && <ServicesTab token={token} refreshContent={refresh} />}
        {tab === "brands" && <BrandsTab token={token} refreshContent={refresh} />}
        {tab === "testimonials" && <TestimonialsTab token={token} refreshContent={refresh} />}
        {tab === "faqs" && <FaqsTab token={token} />}
        {tab === "settings" && <SettingsTab token={token} refreshContent={refresh} />}
      </div>
    </div>
  );
}
